import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Award, Users, Heart, Shield } from 'lucide-react';
import PageTitle from '../hooks/PageTitle';

const CodeOfConduct = () => {
  const sections = [
    { id: '1', title: '1. Introduction', href: '#introduction' },
    { id: '2', title: '2. Professional Excellence', href: '#excellence' },
    { id: '3', title: '3. Integrity and Ethics', href: '#integrity' },
    { id: '4', title: '4. Client Service', href: '#client-service' },
    { id: '5', title: '5. Confidentiality', href: '#confidentiality' },
    { id: '6', title: '6. Diversity and Inclusion', href: '#diversity' },
    { id: '7', title: '7. Regulatory Compliance', href: '#compliance' },
    { id: '8', title: '8. Social Responsibility', href: '#responsibility' },
  ];

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for the highest standards in everything we do, delivering exceptional legal services to our clients.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We act with honesty, transparency, and professionalism in all our dealings, maintaining the trust placed in us.'
    },
    {
      icon: Users,
      title: 'Respect',
      description: 'We treat everyone with dignity and respect, fostering an inclusive environment where diverse perspectives are valued.'
    },
    {
      icon: Heart,
      title: 'Responsibility',
      description: 'We are committed to making a positive impact on society and contributing to the communities we serve.'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageTitle title="Code of Conduct" />
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span>Code of Conduct</span>
          </div>
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white/10 p-3 rounded-lg">
              <Award className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif">Code of Conduct</h1>
          </div>
          <p className="text-lg text-gray-300 max-w-3xl">
            Our commitment to professional excellence, ethical practice, and social responsibility.
          </p>
        </div>
      </div>

      {/* Core Values */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-900 text-center mb-12">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <div className="bg-gray-900 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-4">Sections</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={section.href}
                    className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="prose prose-lg max-w-none">
              {/* Section 1 */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">1. Introduction</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    This Code of Conduct sets out the professional and ethical standards that all members of Ashbourne Law are expected to uphold. It reflects our commitment to excellence, integrity, and social responsibility in the practice of law.
                  </p>
                  <p>
                    Our Code applies to all lawyers, staff, and consultants working at or with Ashbourne Law, regardless of their location or role. It complements the regulatory requirements imposed by the Solicitors Regulation Authority (SRA) and other applicable professional bodies.
                  </p>
                  <p>
                    We believe that adherence to the highest ethical standards is fundamental to maintaining the trust of our clients, the legal profession, and the public.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section id="excellence" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">2. Professional Excellence</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Competence and Expertise</h3>
                  <p>
                    We are committed to providing legal services of the highest quality. All members of the firm must:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Maintain and develop professional knowledge and skills</li>
                    <li>Only undertake work they are competent to handle</li>
                    <li>Seek guidance or refer matters when necessary</li>
                    <li>Keep abreast of legal developments relevant to their practice areas</li>
                    <li>Participate in continuing professional development</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.2 Diligence and Care</h3>
                  <p>
                    We owe our clients a duty of care and must:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Act diligently and promptly on all client matters</li>
                    <li>Provide clear, practical, and timely advice</li>
                    <li>Keep clients informed of progress on their matters</li>
                    <li>Meet agreed deadlines and commitments</li>
                    <li>Maintain accurate and complete records</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.3 Quality Assurance</h3>
                  <p>
                    We maintain robust quality assurance processes to ensure consistent excellence in our work, including regular file reviews, peer supervision, and client feedback mechanisms.
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section id="integrity" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">3. Integrity and Ethics</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Honesty and Transparency</h3>
                  <p>
                    We act with honesty and integrity in all professional dealings. We will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Be truthful in all communications</li>
                    <li>Not mislead clients, courts, or other parties</li>
                    <li>Provide transparent information about fees and costs</li>
                    <li>Disclose any conflicts of interest promptly</li>
                    <li>Admit mistakes and take appropriate corrective action</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">3.2 Independence and Objectivity</h3>
                  <p>
                    We maintain our professional independence and provide objective advice, free from undue influence or personal interests that might compromise our judgment.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">3.3 Conflicts of Interest</h3>
                  <p>
                    We have robust systems to identify and manage conflicts of interest. Where conflicts arise or may arise, we will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Disclose the conflict to all affected parties</li>
                    <li>Obtain informed consent where appropriate</li>
                    <li>Decline to act where the conflict cannot be managed</li>
                    <li>Implement information barriers where necessary</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">3.4 Anti-Bribery and Corruption</h3>
                  <p>
                    We have a zero-tolerance policy towards bribery and corruption. We will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Not offer, promise, give, request, or accept bribes</li>
                    <li>Not make facilitation payments</li>
                    <li>Ensure gifts and hospitality are reasonable and proportionate</li>
                    <li>Maintain clear records of all gifts and hospitality</li>
                    <li>Report any suspicions of bribery or corruption</li>
                  </ul>
                </div>
              </section>

              {/* Section 4 */}
              <section id="client-service" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">4. Client Service</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Client-Centered Approach</h3>
                  <p>
                    We put our clients at the heart of everything we do. We will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Understand our clients' objectives and priorities</li>
                    <li>Provide clear, practical advice tailored to their needs</li>
                    <li>Communicate in plain language, avoiding unnecessary jargon</li>
                    <li>Be responsive and accessible</li>
                    <li>Deliver value for money</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.2 Understanding Client Expectations</h3>
                  <p>
                    At the outset of every engagement, we ensure clients understand:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The scope of our work</li>
                    <li>Who will be handling their matter</li>
                    <li>How we will charge for our services</li>
                    <li>How long the matter is likely to take</li>
                    <li>How to make a complaint if they are unhappy</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.3 Complaints Handling</h3>
                  <p>
                    We take complaints seriously and have a clear, fair procedure for handling them. We will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Listen to concerns and respond promptly</li>
                    <li>Investigate complaints thoroughly and objectively</li>
                    <li>Provide a clear written response</li>
                    <li>Take corrective action where necessary</li>
                    <li>Learn from complaints to improve our services</li>
                  </ul>
                </div>
              </section>

              {/* Section 5 */}
              <section id="confidentiality" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">5. Confidentiality and Data Protection</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 Client Confidentiality</h3>
                  <p>
                    We owe our clients a duty of confidentiality. We will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Keep all client information confidential</li>
                    <li>Only disclose information with client consent or where required by law</li>
                    <li>Implement appropriate security measures to protect information</li>
                    <li>Ensure staff and third parties respect confidentiality obligations</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">5.2 Data Protection</h3>
                  <p>
                    We comply with all applicable data protection laws and regulations, including the UK GDPR. We process personal data fairly, lawfully, and transparently, and only for specified purposes. See our <Link to="/privacy-policy" className="text-gray-900 underline">Privacy Policy</Link> for more information.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">5.3 Information Security</h3>
                  <p>
                    We maintain robust information security systems and practices to protect client data from unauthorized access, loss, or misuse. All staff receive regular training on information security and data protection.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section id="diversity" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">6. Diversity, Inclusion, and Respect</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">6.1 Commitment to Diversity</h3>
                  <p>
                    We are committed to promoting diversity and inclusion in all aspects of our firm. We believe that a diverse workforce and inclusive culture enhance the quality of our services and our ability to serve clients effectively.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">6.2 Equal Opportunities</h3>
                  <p>
                    We provide equal opportunities in recruitment, career development, and promotion based on merit and ability. We do not discriminate on the basis of:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Age</li>
                    <li>Disability</li>
                    <li>Gender reassignment</li>
                    <li>Marriage and civil partnership</li>
                    <li>Pregnancy and maternity</li>
                    <li>Race, ethnicity, or national origin</li>
                    <li>Religion or belief</li>
                    <li>Sex or sexual orientation</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">6.3 Respectful Workplace</h3>
                  <p>
                    We are committed to maintaining a workplace free from harassment, bullying, and discrimination. All members of the firm must:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Treat colleagues, clients, and others with dignity and respect</li>
                    <li>Not engage in any form of harassment or bullying</li>
                    <li>Respect different perspectives and working styles</li>
                    <li>Foster an inclusive environment where everyone can thrive</li>
                    <li>Report any concerns about inappropriate behavior</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">6.4 Flexible Working</h3>
                  <p>
                    We support flexible and modern working arrangements that enable our people to balance professional and personal commitments while delivering excellent client service.
                  </p>
                </div>
              </section>

              {/* Section 7 */}
              <section id="compliance" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">7. Regulatory Compliance</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">7.1 SRA Standards and Regulations</h3>
                  <p>
                    As a firm regulated by the Solicitors Regulation Authority, we comply with the SRA Standards and Regulations, including the SRA Principles and the SRA Code of Conduct for Solicitors, RELs and RFLs.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">7.2 Anti-Money Laundering</h3>
                  <p>
                    We have robust systems and controls to prevent money laundering and terrorist financing. We will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Verify the identity of clients and beneficial owners</li>
                    <li>Conduct due diligence on client relationships</li>
                    <li>Monitor transactions for suspicious activity</li>
                    <li>Report suspicious activity to the National Crime Agency</li>
                    <li>Maintain appropriate records</li>
                    <li>Provide regular training to staff</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">7.3 Sanctions Compliance</h3>
                  <p>
                    We comply with all applicable sanctions regimes and will not act for clients or on matters where doing so would breach sanctions laws.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">7.4 Reporting and Whistleblowing</h3>
                  <p>
                    We encourage the reporting of any concerns about unethical or unlawful conduct. We have a whistleblowing policy that protects individuals who raise genuine concerns in good faith.
                  </p>
                </div>
              </section>

              {/* Section 8 */}
              <section id="responsibility" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">8. Social Responsibility</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mb-3">8.1 Pro Bono and Community Service</h3>
                  <p>
                    We believe in using our legal skills to benefit society. We encourage all members of the firm to participate in pro bono work and community service initiatives.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">8.2 Ashbourne Charity Initiative</h3>
                  <p>
                    Through our <Link to="/social-responsibility" className="text-gray-900 underline">1% for Children's Futures</Link> program, we commit 1% of our annual profits to support educational opportunities for disadvantaged children.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">8.3 Environmental Sustainability</h3>
                  <p>
                    We are committed to minimizing our environmental impact through:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Reducing paper consumption and promoting digital working</li>
                    <li>Minimizing energy use and carbon emissions</li>
                    <li>Responsible procurement and waste management</li>
                    <li>Supporting sustainable business practices</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">8.4 Supporting the Legal Profession</h3>
                  <p>
                    We contribute to the development of the legal profession through mentoring, training the next generation of lawyers, and participating in legal sector initiatives and professional bodies.
                  </p>
                </div>
              </section>

              {/* Enforcement */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8">
                <h3 className="text-xl font-serif text-gray-900 mb-4">Compliance and Enforcement</h3>
                <div className="space-y-3 text-gray-700 text-sm">
                  <p>
                    All members of Ashbourne Law are expected to comply with this Code of Conduct. Breaches of the Code will be taken seriously and may result in disciplinary action, up to and including termination of employment or engagement.
                  </p>
                  <p>
                    The firm's management is responsible for promoting and enforcing this Code. If you have any questions about the Code or wish to report a concern, please contact the Compliance Officer or use our confidential whistleblowing hotline.
                  </p>
                  <p>
                    This Code is reviewed annually and updated as necessary to reflect changes in law, regulation, and best practice.
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-8">
                Approved by the Board of Directors<br />
                Last updated: December 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeOfConduct;